import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { frappePost } from '@/lib/frappe';
import { useLanguage } from '@/contexts/LanguageContext';

interface GoogleAuthUrlResponse {
  success: boolean;
  auth_url?: string;
  message?: string;
  error_code?: string;
}

export const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      fill="#EA4335"
    />
  </svg>
);

interface GoogleAuthButtonProps {
  /** Role carried through the OAuth round-trip; omit on the login page. */
  role?: 'student' | 'teacher';
  label: string;
  disabled?: boolean;
}

/**
 * "Continue with Google" button: fetches the Google authorize URL from the
 * backend (which encodes the intended role in the OAuth state) and redirects
 * the whole window there. The flow returns via /auth/google/callback.
 */
export const GoogleAuthButton = ({ role, label, disabled }: GoogleAuthButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const startGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const response = await frappePost<GoogleAuthUrlResponse>(
        'hudan_lms.api.google_auth.get_google_auth_url',
        role ? { role } : {},
        false
      );
      if (response.success && response.auth_url) {
        window.location.assign(response.auth_url);
        return; // keep the spinner until the browser navigates away
      }
      toast({
        title: 'Google',
        description: response.message || t('auth.google.unavailable'),
        variant: 'destructive',
      });
    } catch {
      toast({
        title: 'Google',
        description: t('auth.google.unavailable'),
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full rounded-lg h-11 font-medium gap-2"
      onClick={startGoogleAuth}
      disabled={disabled || isLoading}
    >
      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
      {label}
    </Button>
  );
};

/** Thin "── or ──" separator used between the Google button and the form. */
export const AuthDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3" role="separator">
    <span className="h-px flex-1 bg-border" />
    <span className="text-xs text-muted-foreground uppercase">{label}</span>
    <span className="h-px flex-1 bg-border" />
  </div>
);
